import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/utils";
import { ArrowDownAZ, Layers, ListFilter, Plus } from "lucide-react";
import {
  CONTENTS_KIND_LABELS,
  type ContentsKind,
  type ContentsSort,
} from "./canvasRailContentsModel";

const KIND_ORDER: ContentsKind[] = [
  "sections",
  "files",
  "entities",
  "shapes",
  "notes",
  "labels",
  "widgets",
];

const HEAD_CTRL = cn(
  "inline-flex size-7 items-center justify-center rounded-md text-n-50",
  "hover:bg-n-10 hover:text-n-80",
  "data-[state=open]:bg-n-10 data-[state=open]:text-n-80",
);

interface CanvasRailContentsControlsProps {
  canvasId: string;
  canEdit: boolean;
  hiddenKinds: Set<ContentsKind>;
  onToggleKind: (kind: ContentsKind) => void;
  sort: ContentsSort;
  onChangeSort: (next: ContentsSort) => void;
  /** The head's ＋ — reopens the canvas add-context flyout (back on the
   *  head by designer ruling, Aug 2026). */
  onAddContext?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * The Canvas Contents head controls (DS 021's CONTENT_CTRL pair): the
 * filter-and-sort menu — Show hides whole folders, Sort orders within one —
 * and the ＋ add glyph. Both hover-revealed by the section head's group.
 */
export function CanvasRailContentsControls({
  canEdit,
  hiddenKinds,
  onToggleKind,
  sort,
  onChangeSort,
  onAddContext,
}: CanvasRailContentsControlsProps) {
  return (
    <div className="flex items-center gap-0.5">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            title="Filter and sort contents"
            aria-label="Filter and sort contents"
            className={HEAD_CTRL}
          >
            <ListFilter className="size-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Show
          </DropdownMenuLabel>
          {KIND_ORDER.map((kind) => (
            <DropdownMenuCheckboxItem
              key={kind}
              checked={!hiddenKinds.has(kind)}
              onSelect={(e) => {
                e.preventDefault();
                onToggleKind(kind);
              }}
            >
              {CONTENTS_KIND_LABELS[kind]}
            </DropdownMenuCheckboxItem>
          ))}

          <DropdownMenuSeparator />

          {/* Sort orders WITHIN a folder — the folders carry kind (DS 021). */}
          <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Sort
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={sort}
            onValueChange={(v) => onChangeSort(v as ContentsSort)}
          >
            <DropdownMenuRadioItem
              value="layer"
              className="gap-2"
              onSelect={(e) => e.preventDefault()}
            >
              <Layers className="h-3.5 w-3.5 text-muted-foreground" />
              Layer order
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem
              value="name"
              className="gap-2"
              onSelect={(e) => e.preventDefault()}
            >
              <ArrowDownAZ className="h-3.5 w-3.5 text-muted-foreground" />
              Name
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      {canEdit && onAddContext && (
        <button
          type="button"
          title="Add context"
          aria-label="Add context"
          // The imperative open-add-context path (onboarding, insert menu)
          // anchors its popover at this element via the selector in
          // canvasRailAddContextEvents; without the stamp it falls back to
          // the board's center.
          data-canvas-add-context-trigger=""
          onClick={onAddContext}
          className={HEAD_CTRL}
        >
          <Plus className="size-3.5" />
        </button>
      )}
    </div>
  );
}
